/**
 * photo-editor / index.js — bootstrap and wiring.
 *
 * Owns the things that are about THIS page rather than about editing: reading
 * the file in, mounting the panels, keyboard shortcuts, the daily cap, the
 * telemetry funnel, batch, and saving.
 *
 * The one behavioural decision worth naming here is what happens the moment a
 * file lands: the photo goes straight onto the canvas and every control becomes
 * live. There is no "now press Process".
 *
 * That is a direct answer to the largest hole in our live telemetry. `uploading`
 * — the funnel stage meaning "a file was picked and nothing was ever done with
 * it" — is the top abandon stage on EVERY photo tool we run: /photo-repair 1556
 * sessions in 30 days, /photo-colorize 789, /photo-enhancer 487,
 * /social-photo-resize 433, /passport-photo 302, /thumbnail-maker 234. Those
 * tools all have a dead moment between picking a file and getting anything
 * back, and a third of the people who reach it leave. A document-shaped editor
 * does not have that moment to lose them in.
 */

'use strict';

import * as Store from './app/store.js';
import * as View from './app/viewport.js';
import * as P from './app/panels.js';
import * as Doc from './core/doc.js';
import * as Mask from './core/mask.js';
import { identityParams, stringifyCube, parseCube, buildLUT } from './core/color.js';
import { decodeFile } from '../merge-images/decode.js';
import { exportDoc, exportWarnings, presetSize, PRESETS as EXPORT_PRESETS, FORMATS, formatSupported } from './io/export.js';
import { saveProject, loadProject, PROJECT_EXT } from './io/project.js';
import { OPS } from './ops/index.js';
import * as Session from './app/session.js';
import { createRegistry } from './render/compose.js';

const SLUG = 'photo-editor';

/* ─── i18n ────────────────────────────────────────────────────────────────── */

const I18N = window.PE_I18N || {};
/** Translation lookup. A missing key returns the key, which is loud in review
 *  and harmless in production — the alternative, an empty string, produces an
 *  unlabelled control nobody notices until a user asks what it does. */
const T = (k) => (I18N[k] != null ? I18N[k] : k);

const $ = (id) => document.getElementById(id);
const toast = (m, t) => (window.__toast ? window.__toast(m, t) : console.log('[toast]', t, m));
const stage = (s, d) => { try { window.__stage && window.__stage(s, d); } catch (_) { } };
const funnel = (s, d) => { try { window.__aeFunnel && window.__aeFunnel(s, d); } catch (_) { } };

/* ─── boot ────────────────────────────────────────────────────────────────── */

const store = Store.createStore();
let vp = null;
let ui = {};
let exportOpts = { format: 'png', quality: 0.92, preset: 'original', targetKB: 0 };
let batchFiles = [];

document.addEventListener('DOMContentLoaded', init);

function init() {
    const canvas = $('peCanvas');
    if (!canvas) return;

    vp = View.createViewport(canvas, store, {
        onChange: () => { if (ui.zoomLabel) ui.zoomLabel.textContent = Math.round(vp.zoom * 100) + '%'; },
        onPick: (c) => onEyedrop(c),
        onError: (err) => {
            const msg = String(err && err.message || err);
            toast(msg === 'needs_image_layer' ? T('err.needs_image_layer')
                : T('err.op_failed').replace(':op', T('tool.spot')), 'error');
            funnel('spot_error', { err: msg.slice(0, 60) });
        },
    });

    buildPanels();
    bindRail();
    bindTopbar();
    bindFocusAndMenu();
    bindUploader();
    bindKeyboard();
    bindPaste();

    // Sync the DOM to the initial tool rather than trusting the markup's
    // `hidden` attributes to already agree with `vp.tool` — two sources of
    // truth for the same state is how a panel ends up visible for a tool that
    // is not selected.
    setTool(vp.tool);

    setCompare = Session.createCompare(store, Store.emit);

    Store.subscribe(store, (what) => {
        if (what === 'open') { View.fit(vp); }
        if (what === 'doc' || what === 'open') scheduleSessionSave();
        if (what === 'busy') { renderBusy(); return; }
        View.draw(vp);
        syncPanels();
    });

    stage('ready');
    offerRestore();
}

/* ─── input ───────────────────────────────────────────────────────────────── */

function bindUploader() {
    const zone = $('peDrop');
    if (!zone) return;
    zone.addEventListener('tool-uploader:file', async (e) => {
        const files = e.detail.files && e.detail.files.length ? e.detail.files : [e.detail.file];
        await openFiles(files, e.detail.source);
    });
    zone.addEventListener('tool-uploader:clear', () => {
        document.querySelector('.pe-editor')?.setAttribute('hidden', '');
        document.querySelector('.pe-drop')?.removeAttribute('hidden');
    });
}

/**
 * The reading state.
 *
 * ⚠️ This exists because of a real report: "the file doesn't seem to load".
 * It did load — it just took several seconds with NOTHING on screen. Decoding a
 * 4000×3000 JPEG takes a moment, and an iPhone HEIC has to pull a WASM decoder
 * first (measured: >3.5 s before anything appeared). A person watching a dead
 * page for that long has already concluded the tool is broken, and they are
 * right to: silence is indistinguishable from failure.
 *
 * This is the same hole the whole tool was built to close — `uploading` is the
 * top abandon stage on every one of our photo tools — and the first version
 * only closed it AFTER the decode. Now the gap is covered end to end.
 */
function showReading(name, stage) {
    const box = $('peReading');
    if (!box) return;
    box.hidden = false;
    const n = $('peReadingName'), st = $('peReadingStage');
    if (n && name) n.textContent = name;
    if (st) st.textContent = stage || T('msg.reading');
}

function hideReading() {
    const box = $('peReading');
    if (box) box.hidden = true;
}

async function openFiles(files, source) {
    if (!files || !files.length) return;
    funnel('file_select', { n: files.length, source });

    // A project file reopens the whole document; anything else is a picture.
    const proj = files.find((f) => /\.(tmbphoto|zip)$/i.test(f.name || ''));
    if (proj) {
        showReading(proj.name, T('msg.opening_project'));
        try { await openProject(proj); } finally { hideReading(); }
        return;
    }

    const f0 = files[0];
    showReading(f0.name, T('msg.reading'));
    // HEIC has to fetch a WASM decoder before it can even start, so say so
    // rather than letting the wait look like a hang.
    if (/\.(heic|heif)$/i.test(f0.name || '') || /heic|heif/i.test(f0.type || '')) {
        showReading(f0.name, T('msg.reading_heic'));
    }

    try {
        const first = await decodeFile(files[0], {
            emit: (k, d) => {
                funnel(k, d);
                // The decode chain announces its own escalations; surfacing them
                // turns "still nothing" into "still working, and here is why".
                if (k === 'decode_tier2') showReading(f0.name, T('msg.reading_retry'));
                if (k === 'decode_tier3') showReading(f0.name, T('msg.reading_heic'));
            },
        });
        Store.openImage(store, first.drawable, {
            name: first.name, hasAlpha: first.hasAlpha, file: files[0],
        });
        if (first.downscaled) toast(T('msg.downscaled'), 'info');
        if (first.animated) toast(T('msg.animated_first_frame'), 'info');

        // More than one file is a BATCH, not a mistake: the first opens for
        // editing and the rest queue up to receive the same recipe. That is the
        // entire batch feature — it falls out of the recipe model rather than
        // being a separate mode with its own UI to learn.
        batchFiles = files.slice(1);
        hideReading();
        showEditor();
        syncBatchBar();
        stage('editing');
    } catch (err) {
        hideReading();
        toast(decodeMessage(err), 'error');
        funnel('decode_failed', { code: (err && err.code) || 'UNKNOWN' });
    }
}

function decodeMessage(err) {
    const code = (err && err.code) || '';
    if (code === 'NOT_IMAGE') return T('err.not_image');
    if (code === 'HEIC_FAILED') return T('err.heic');
    if (code === 'IMAGE_DECODE_FAILED') return T('err.decode');
    return T('err.decode');
}

function showEditor() {
    document.querySelector('.pe-drop')?.setAttribute('hidden', '');
    const ed = document.querySelector('.pe-editor');
    ed?.removeAttribute('hidden');
    requestAnimationFrame(() => View.fit(vp));
    // On a phone the editor is otherwise ~1.7 screens below the fold, so
    // choosing a photo would be followed by an apparently unchanged page. This
    // is deliberately NOT a scroll (the house rule forbids moving the page for
    // the user, and it would be the wrong fix anyway) — the workspace takes the
    // screen, and one clearly labelled button gives the page back.
    if (isNarrow()) setFocus(true);
}

/* ─── focus mode ──────────────────────────────────────────────────────────── */

const NARROW = '(max-width: 900px)';
function isNarrow() {
    try { return window.matchMedia(NARROW).matches; } catch (_) { return false; }
}

function setFocus(on) {
    const app = document.querySelector('.pe-app');
    if (!app) return;
    // Entering while the tool is still on the intake screen would put a fixed
    // empty panel over the page — nothing to edit, no way to understand it.
    if (on && document.querySelector('.pe-editor')?.hasAttribute('hidden')) return;
    app.classList.toggle('is-focus', !!on);
    // The sheet is a focus-mode concept; leaving it set would make the next
    // entry open on a panel the user never asked for.
    if (!on) app.classList.remove('is-sheet');
    document.body.classList.toggle('pe-focus-lock', !!on);
    const btn = $('peFocus');
    if (btn) btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    const label = $('peFocusLabel');
    if (label) label.textContent = T(on ? 'ui.focus_off' : 'ui.focus_on');
    closeMore();
    // The stage just changed size by an order of magnitude. A plain redraw
    // would keep the old zoom and show a corner of the photo.
    requestAnimationFrame(() => View.fit(vp));
}

function closeMore() {
    document.querySelector('.pe-tb-more')?.classList.remove('is-open');
    $('peMore')?.setAttribute('aria-expanded', 'false');
}

function bindFocusAndMenu() {
    $('peFocus')?.addEventListener('click', () => {
        setFocus(!document.querySelector('.pe-app')?.classList.contains('is-focus'));
    });

    const more = document.querySelector('.pe-tb-more');
    $('peMore')?.addEventListener('click', (e) => {
        e.stopPropagation();
        const open = !more.classList.contains('is-open');
        more.classList.toggle('is-open', open);
        $('peMore').setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    // Any command inside the menu is a completed choice; leaving it open on top
    // of the canvas would hide the very thing the command just changed.
    $('peMoreMenu')?.addEventListener('click', closeMore);
    document.addEventListener('click', (e) => {
        if (more && !more.contains(e.target)) closeMore();
    });

    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (document.querySelector('.pe-tb-more')?.classList.contains('is-open')) return closeMore();
        if (document.querySelector('.pe-app')?.classList.contains('is-focus')) setFocus(false);
    });

    // A window that grows past the breakpoint (rotation, a desktop resize, a
    // tablet keyboard) must not leave the user inside a mode whose exit button
    // the wide layout hides.
    try {
        window.matchMedia(NARROW).addEventListener('change', (ev) => {
            if (!ev.matches) setFocus(false);
        });
    } catch (_) { /* Safari < 14 has no addEventListener on MediaQueryList */ }
}

/**
 * Paste from the clipboard.
 *
 * Screenshot → Ctrl+V is how a large share of "edit this image" actually
 * starts, and a tool that only accepts a file picker makes people save to disk
 * first for no reason.
 */
function bindPaste() {
    window.addEventListener('paste', async (e) => {
        const items = [...(e.clipboardData?.items || [])];
        const it = items.find((i) => i.type.startsWith('image/'));
        if (!it) return;
        const f = it.getAsFile();
        if (!f) return;
        e.preventDefault();
        if (store.doc.layers.length) {
            const d = await decodeFile(f, {});
            Store.addImageLayer(store, d.drawable, { name: d.name, file: f });
            toast(T('msg.pasted_layer'), 'success');
        } else {
            await openFiles([f], 'paste');
        }
    });
}

/* ─── the rail ────────────────────────────────────────────────────────────── */

function bindRail() {
    document.querySelectorAll('[data-tool]').forEach((btn) => {
        btn.addEventListener('click', () => setTool(btn.dataset.tool));
    });
}

function setTool(tool) {
    vp.tool = tool;
    vp.showMask = tool === 'brush' || tool === 'wand' || tool === 'lasso';
    if (tool === 'crop' && !vp.cropRect) {
        vp.cropRect = { x: 0, y: 0, w: store.doc.w, h: store.doc.h };
    }
    document.querySelectorAll('[data-tool]').forEach((b) => {
        b.classList.toggle('is-on', b.dataset.tool === tool);
        b.setAttribute('aria-pressed', String(b.dataset.tool === tool));
    });
    document.querySelectorAll('[data-toolopts]').forEach((n) => {
        n.hidden = n.dataset.toolopts !== tool;
    });
    View.draw(vp);
}

/* ─── topbar ──────────────────────────────────────────────────────────────── */

function bindTopbar() {
    ui.zoomLabel = $('peZoom');
    $('peUndo')?.addEventListener('click', () => Store.undo(store));
    $('peRedo')?.addEventListener('click', () => Store.redo(store));
    $('peZoomIn')?.addEventListener('click', () => View.setZoom(vp, vp.zoom * 1.25));
    $('peZoomOut')?.addEventListener('click', () => View.setZoom(vp, vp.zoom / 1.25));
    $('peZoomFit')?.addEventListener('click', () => View.fit(vp));
    $('peZoom100')?.addEventListener('click', () => View.setZoom(vp, 1));
    $('peSaveProject')?.addEventListener('click', doSaveProject);
    $('peCopy')?.addEventListener('click', copyToClipboard);

    /* Hold to see the original.
       Every photo app has this and it is the single cheapest way to answer
       "have I actually improved it?". Implemented by hiding every layer's grade
       and ops for as long as the control is held — no extra render path, no
       second document. Pointer AND keyboard (the \ key), because a
       hold-to-compare that needs a mouse excludes exactly the people who edit
       by keyboard. */
    const cmp = $('peCompare');
    if (cmp) {
        const down = (e) => { e.preventDefault(); setCompare(true); };
        const up = () => setCompare(false);
        cmp.addEventListener('pointerdown', down);
        cmp.addEventListener('pointerup', up);
        cmp.addEventListener('pointerleave', up);
        cmp.addEventListener('pointercancel', up);
    }
    $('peOpenProject')?.addEventListener('click', () => $('peProjectInput')?.click());
    $('peProjectInput')?.addEventListener('change', (e) => {
        const f = e.target.files && e.target.files[0];
        if (f) openProject(f);
        e.target.value = '';
    });
}

function bindKeyboard() {
    window.addEventListener('keyup', (e) => {
        if (isCompareKey(e)) setCompare(false);
    });
    window.addEventListener('keydown', (e) => {
        const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName) || e.target.isContentEditable;
        if (typing) return;
        const mod = e.ctrlKey || e.metaKey;
        const kl = window.__keyLetter ? window.__keyLetter(e) : String(e.key || '').toLowerCase();

        if (mod && kl === 'z') {
            e.preventDefault();
            (e.shiftKey ? Store.redo : Store.undo)(store);
            return;
        }
        if (mod && kl === 'y') { e.preventDefault(); Store.redo(store); return; }
        if (mod && kl === 's') { e.preventDefault(); doSaveProject(); return; }
        if (mod && kl === 'd') { e.preventDefault(); View.clearSelection(vp); return; }
        if (mod && kl === 'a') {
            e.preventDefault();
            const m = Mask.createMask(store.doc.w, store.doc.h, 255, 'doc');
            View.setSelection(vp, m, 'replace');
            return;
        }
        if (mod && kl === 'i') {
            e.preventDefault();
            if (store.selection) { Mask.invert(store.selection); View.draw(vp); }
            return;
        }
        if (e.key === 'Delete' || e.key === 'Backspace') {
            const l = Doc.activeLayer(store.doc);
            if (l && store.doc.layers.length > 1) { e.preventDefault(); Store.removeActive(store); }
            return;
        }
        // Single-key tools, the shortcuts every editor shares — muscle memory
        // is worth more here than originality.
        const map = { v: 'move', c: 'crop', b: 'brush', j: 'spot', w: 'wand', l: 'lasso', i: 'eyedrop', h: 'hand', t: 'text' };
        if (!mod && map[kl]) {
            if (map[kl] === 'text') { Store.addText(store, T('ui.your_text')); setTool('move'); }
            else setTool(map[kl]);
        }
        if (!mod && (e.key === '[' || e.key === ']')) {
            // Resize the tool actually in hand. Always reaching for the mask
            // brush means the bracket keys silently do nothing while the spot
            // tool is selected — the classic "shortcut is broken" report.
            const g = vp.tool === 'spot' ? vp.spot : vp.brush;
            const lo = vp.tool === 'spot' ? 2 : 2, hi = vp.tool === 'spot' ? 200 : 600;
            g.radius = Math.max(lo, Math.min(hi, g.radius * (e.key === '[' ? 0.8 : 1.25)));
            ui.brushSize && (ui.brushSize.value = Math.round(vp.brush.radius));
            View.draw(vp);
        }
        if (!mod && isCompareKey(e)) { setCompare(true); return; }
        if (!mod && e.key === '0') View.fit(vp);
        if (!mod && e.key === '1') View.setZoom(vp, 1);
    });
}


/* ─── compare / clipboard / restore ───────────────────────────────────────── */

const isCompareKey = Session.isCompareKey;
let setCompare = () => {};

/**
 * Put the result on the clipboard.
 *
 * Exported at the CURRENT export size so what lands in the paste is what the
 * export panel promises — a "copy" that silently differs from "export" is a
 * trap people only notice after sending it to someone.
 */
async function copyToClipboard() {
    if (!store.doc.layers.length) return;
    const btn = $('peCopy');
    if (btn) btn.disabled = true;
    try {
        const res = await exportDoc(store.doc, store.reg, {
            format: 'png', size: currentExportSize(), name: 'clip',
        });
        await Session.copyBlobToClipboard(res.blob);
        toast(T('msg.copied'), 'success');
        funnel('copy_clipboard', { w: res.width, h: res.height, bytes: res.bytes });
    } catch (e) {
        // Safari only permits a clipboard write inside a user gesture, and an
        // await can outlive one. Naming the failure beats a bare "error".
        toast(T('err.clipboard'), 'error');
        console.warn('[photo-editor] clipboard', e && e.message);
    } finally {
        if (btn) btn.disabled = false;
    }
}

let _saveTimer = null;
function scheduleSessionSave() {
    if (_saveTimer) clearTimeout(_saveTimer);
    // Debounced: a slider drag must not write to IndexedDB sixty times a second.
    _saveTimer = setTimeout(() => Session.saveSession(store), 1500);
}

async function offerRestore() {
    const rec = await Session.peekSession();
    const bar = $('peRestore');
    if (!rec || !bar) return;
    const label = $('peRestoreName');
    if (label) {
        label.textContent = rec.name + ' \u00b7 ' + new Date(rec.at).toLocaleString(
            window.__LOCALE || undefined,
            { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    }
    bar.hidden = false;
    funnel('restore_offered', {});

    $('peRestoreGo')?.addEventListener('click', async () => {
        bar.hidden = true;
        showReading(rec.name, T('msg.restoring'));
        try {
            const r = await Session.restoreSession(rec, decodeFile, createRegistry);
            store.doc = r.doc;
            store.reg = r.reg;
            store.sources = r.sources;
            store.hist = Store.Hist.createHistory(r.doc);
            Store.emit(store, 'open');
            showEditor();
            toast(T('msg.restored'), 'success');
            funnel('session_restored', { layers: r.doc.layers.length });
        } catch (e) {
            toast(T('err.restore'), 'error');
            console.warn('[photo-editor] restore', e && e.message);
        } finally { hideReading(); }
    }, { once: true });

    $('peRestoreNo')?.addEventListener('click', () => {
        bar.hidden = true;
        Session.clearSession();
    }, { once: true });
}

/* ─── panels ──────────────────────────────────────────────────────────────── */

function buildPanels() {
    buildAdjustPanel();
    buildLayersPanel();
    buildAiPanel();
    buildExportPanel();
    document.querySelectorAll('[data-pane-tab]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const app = document.querySelector('.pe-app');
            const wasActive = btn.classList.contains('is-on');
            document.querySelectorAll('[data-pane-tab]').forEach((b) => {
                b.classList.toggle('is-on', b === btn);
                b.setAttribute('aria-selected', String(b === btn));
            });
            document.querySelectorAll('[data-pane]').forEach((p) => {
                p.hidden = p.dataset.pane !== btn.dataset.paneTab;
            });
            // In focus mode the panel is a bottom sheet, closed by default: on a
            // 664px-tall phone an always-open panel left the canvas 184px high —
            // a quarter of the screen, for the one element the tool is FOR.
            // Tapping a tab opens it; tapping the open tab again gives the
            // canvas back, so the picture is never more than one tap away.
            if (app?.classList.contains('is-focus')) {
                const open = !(wasActive && app.classList.contains('is-sheet'));
                app.classList.toggle('is-sheet', open);
                requestAnimationFrame(() => View.draw(vp));
            }
        });
    });
}

const gradeOf = () => (Doc.activeLayer(store.doc)?.grade) || identityParams();
const spatialOf = () => (Doc.activeLayer(store.doc)?.spatial) || Doc.defaultSpatial();

function buildAdjustPanel() {
    const pane = $('peAdjust');
    if (!pane) return;
    ui.sliders = {};

    ui.histCanvas = P.el('canvas', { class: 'pe-hist' });
    const autoBtn = P.el('button', { class: 'pe-btn pe-btn-wide', type: 'button', text: T('ui.auto'), onclick: () => Store.autoAdjust(store) });
    const resetBtn = P.el('button', {
        class: 'pe-btn', type: 'button', text: T('ui.reset_all'),
        onclick: () => {
            const l = Doc.activeLayer(store.doc);
            if (l) Store.set(store, Doc.updateLayer(store.doc, l.id, { grade: identityParams(), spatial: Doc.defaultSpatial() }), 'reset');
        },
    });

    pane.append(
        P.el('div', { class: 'pe-histwrap' }, [ui.histCanvas]),
        P.el('div', { class: 'pe-row' }, [autoBtn, resetBtn]),
        P.section(T('sec.presets'), [P.presetStrip(T, (id) => Store.applyPreset(store, id))]),
        P.section(T('sec.light'), LIGHT_ROWS()),
        P.section(T('sec.color'), COLOR_ROWS()),
        P.section(T('sec.hsl'), [hslNode()], { open: false }),
        P.section(T('sec.curves'), [curveNode()], { open: false }),
        P.section(T('sec.detail'), DETAIL_ROWS(), { open: false }),
        P.section(T('sec.effects'), EFFECT_ROWS(), { open: false }),
        P.section(T('sec.lut'), lutNode(), { open: false }),
    );
}

function gradeRows(specs) {
    return specs.map((spec) => {
        const row = P.slider(spec, T, gradeOf()[spec.key], (v) => {
            if (spec.spatial) Store.setSpatial(store, { [spec.key]: v }, { live: true, key: spec.key });
            else Store.setGrade(store, { [spec.key]: v }, { live: true, key: spec.key });
        }, (v) => {
            if (spec.spatial) Store.setSpatial(store, { [spec.key]: v });
            else Store.setGrade(store, { [spec.key]: v });
            Store.endGesture(store);
        });
        ui.sliders[spec.key] = row;
        return row;
    });
}

const LIGHT_ROWS = () => gradeRows(P.LIGHT);
const COLOR_ROWS = () => gradeRows(P.COLOR);
const DETAIL_ROWS = () => gradeRows(P.DETAIL);
const EFFECT_ROWS = () => gradeRows(P.EFFECTS);

function hslNode() {
    ui.hsl = P.hslPanel(T, () => gradeOf().hsl, (i, mode, v, live) => {
        const hsl = gradeOf().hsl.map((b, k) => (k === i ? { ...b, [mode]: v } : b));
        Store.setGrade(store, { hsl }, { live, key: 'hsl' + i + mode });
        if (!live) Store.endGesture(store);
    });
    return ui.hsl;
}

function curveNode() {
    const wrap = P.el('div', { class: 'pe-curvewrap' });
    let channel = 'rgb';
    const tabs = P.el('div', { class: 'pe-hsl-tabs' });
    for (const ch of ['rgb', 'r', 'g', 'b']) {
        tabs.appendChild(P.el('button', {
            class: 'pe-hsl-tab' + (ch === 'rgb' ? ' is-on' : ''), type: 'button', text: T('curve.' + ch),
            onclick: (e) => {
                channel = ch;
                tabs.querySelectorAll('.pe-hsl-tab').forEach((b) => b.classList.remove('is-on'));
                e.currentTarget.classList.add('is-on');
                ui.curve.redraw();
            },
        }));
    }
    ui.curve = P.curveEditor(
        () => gradeOf().curves[channel],
        (pts) => {
            const curves = { ...gradeOf().curves, [channel]: pts };
            Store.setGrade(store, { curves }, { live: true, key: 'curve' + channel });
        },
        T,
    );
    wrap.append(tabs, ui.curve.node);
    requestAnimationFrame(() => ui.curve.redraw());
    return wrap;
}

/**
 * LUT in / out.
 *
 * Loading someone else's .cube and exporting your own grade as one are two
 * lines of code here because the colour pipeline already IS a 3-D table. No
 * competitor in this segment offers either, and for anyone who colour-grades
 * video it is the difference between "a filter app" and "a tool".
 */
function lutNode() {
    const file = P.el('input', { type: 'file', accept: '.cube', class: 'pe-hidden-input' });
    file.addEventListener('change', async (e) => {
        const f = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!f) return;
        try {
            const lut = parseCube(await f.text());
            store.loadedLut = lut;
            toast(T('msg.lut_loaded').replace(':n', String(lut.size)), 'success');
        } catch (err) {
            toast(T('err.lut_' + (String(err.message).replace('cube_', '') || 'bad')) || T('err.lut_bad'), 'error');
        }
    });
    return [
        P.el('div', { class: 'pe-row' }, [
            P.el('button', { class: 'pe-btn', type: 'button', text: T('ui.load_lut'), onclick: () => file.click() }),
            P.el('button', {
                class: 'pe-btn', type: 'button', text: T('ui.save_lut'),
                onclick: () => {
                    const lut = buildLUT(gradeOf(), 33);
                    const blob = new Blob([stringifyCube(lut, store.doc.meta.name || 'Timbrica')], { type: 'text/plain' });
                    download(blob, (store.doc.meta.name || 'look') + '.cube');
                },
            }),
        ]),
        file,
        P.el('p', { class: 'pe-hint', text: T('hint.lut') }),
    ];
}

/* ─── layers panel ────────────────────────────────────────────────────────── */

function buildLayersPanel() {
    const pane = $('peLayers');
    if (!pane) return;
    ui.layerList = P.el('div', { class: 'pe-layers' });

    const add = P.el('div', { class: 'pe-row pe-row-wrap' }, [
        P.el('button', { class: 'pe-btn', type: 'button', text: T('ui.add_text'), onclick: () => { Store.addText(store, T('ui.your_text')); setTool('move'); } }),
        P.el('button', { class: 'pe-btn', type: 'button', text: T('ui.add_image'), onclick: () => $('peLayerInput')?.click() }),
        P.el('button', { class: 'pe-btn', type: 'button', text: T('ui.add_adjust'), onclick: () => Store.addAdjustLayer(store) }),
    ]);
    const shapes = P.el('div', { class: 'pe-row pe-row-wrap' },
        ['rect', 'ellipse', 'line', 'arrow', 'triangle', 'star'].map((k) =>
            P.el('button', { class: 'pe-btn pe-btn-icon', type: 'button', title: T('shape.' + k), text: T('shape.' + k), onclick: () => { Store.addShape(store, k); setTool('move'); } })));

    ui.layerProps = P.el('div', { class: 'pe-layerprops' });
    pane.append(add, shapes, ui.layerList, ui.layerProps);

    $('peLayerInput')?.addEventListener('change', async (e) => {
        const f = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!f) return;
        const d = await decodeFile(f, {});
        Store.addImageLayer(store, d.drawable, { name: d.name, file: f });
    });
}

function renderLayers() {
    if (!ui.layerList) return;
    ui.layerList.textContent = '';
    // Top of the stack first: the list reads the way the picture is stacked.
    const layers = store.doc.layers.slice().reverse();
    for (const l of layers) {
        const row = P.el('div', {
            class: 'pe-layer' + (l.id === store.doc.activeId ? ' is-on' : ''),
            draggable: true, 'data-id': l.id,
            onclick: () => Store.selectLayer(store, l.id),
        }, [
            P.el('button', {
                class: 'pe-layer-vis', type: 'button', 'aria-label': T('ui.toggle_visible'),
                text: l.visible ? '👁' : '—',
                onclick: (e) => { e.stopPropagation(); Store.set(store, Doc.updateLayer(store.doc, l.id, { visible: !l.visible }), 'visibility'); },
            }),
            P.el('span', { class: 'pe-layer-name', text: l.name || l.type }),
            l.maskId ? P.el('span', { class: 'pe-layer-badge', text: T('ui.mask'), title: T('ui.has_mask') }) : null,
            (l.ops && l.ops.length) ? P.el('span', { class: 'pe-layer-badge', text: String(l.ops.length), title: T('ui.steps') }) : null,
        ]);
        row.addEventListener('dragstart', (e) => e.dataTransfer.setData('text/plain', l.id));
        row.addEventListener('dragover', (e) => e.preventDefault());
        row.addEventListener('drop', (e) => {
            e.preventDefault();
            const id = e.dataTransfer.getData('text/plain');
            if (!id || id === l.id) return;
            const target = store.doc.layers.findIndex((x) => x.id === l.id);
            Store.reorder(store, id, target);
        });
        ui.layerList.appendChild(row);
    }
    renderLayerProps();
}

function renderLayerProps() {
    const box = ui.layerProps;
    if (!box) return;
    box.textContent = '';
    const l = Doc.activeLayer(store.doc);
    if (!l) return;

    const opacity = P.slider({ key: 'opacity', min: 0, max: 100, step: 1, def: 100 }, T,
        Math.round(l.opacity * 100),
        (v) => Store.updateActive(store, { opacity: v / 100 }, { live: true, key: 'opacity' }),
        (v) => { Store.updateActive(store, { opacity: v / 100 }); Store.endGesture(store); });

    const blend = P.el('select', {
        class: 'pe-select', 'aria-label': T('ui.blend'),
        onchange: (e) => Store.updateActive(store, { blend: e.target.value }),
    }, Doc.BLEND_MODES.map((m) => P.el('option', { value: m, text: T('blend.' + m), selected: m === l.blend })));

    // ⚠️ `.filter(Boolean)`, and it is load-bearing. `P.el()` skips null KIDS,
    // but this is `Element.append()` — the DOM method stringifies null and
    // inserts the literal word "null" as a text node. Every image layer without
    // ops has been printing one under its buttons since the tool was built
    // (caught on prod 2026-08-28, three weeks in); the branches below add more
    // slots that can be null, so the fix belongs here rather than in each one.
    box.append(...[
        P.el('div', { class: 'pe-row' }, [
            P.el('input', {
                class: 'pe-input', value: l.name || '', 'aria-label': T('ui.layer_name'),
                onchange: (e) => Store.updateActive(store, { name: e.target.value }),
            }),
        ]),
        opacity,
        P.el('label', { class: 'pe-field' }, [P.el('span', { text: T('ui.blend') }), blend]),
        P.el('div', { class: 'pe-row pe-row-wrap' }, [
            P.el('button', { class: 'pe-btn', type: 'button', text: T('ui.duplicate'), onclick: () => Store.duplicateActive(store) }),
            P.el('button', { class: 'pe-btn', type: 'button', text: T('ui.delete'), onclick: () => Store.removeActive(store) }),
            l.maskId
                ? P.el('button', { class: 'pe-btn', type: 'button', text: l.maskInverted ? T('ui.mask_normal') : T('ui.mask_invert'), onclick: () => Store.updateActive(store, { maskInverted: !l.maskInverted }) })
                : null,
            store.selection
                ? P.el('button', { class: 'pe-btn', type: 'button', text: T('ui.selection_to_mask'), onclick: () => View.selectionToMask(vp) })
                : null,
        ]),
        l.type === 'text' ? textProps(l) : null,
        l.type === 'image' ? placedProps(l) : null,
        (l.ops && l.ops.length) ? opsList(l) : null,
    ].filter(Boolean));
}

/**
 * The two controls that decide whether a cut-out looks placed or pasted.
 *
 * Both only make sense for a layer with something UNDERNEATH it, which is why
 * they live here and not in the tone panel: the bottom photo has no backdrop to
 * match and casts its shadow on nothing. Hiding them there rather than
 * disabling them keeps the panel of a single-photo document exactly as short as
 * it is today — this tool's own measurement is that people open it for one job.
 */
function placedProps(l) {
    const idx = store.doc.layers.findIndex((x) => x.id === l.id);
    if (idx <= 0) return null;

    const match = P.el('button', {
        class: 'pe-btn', type: 'button', text: T('ui.match_backdrop'),
        title: T('hint.match_backdrop'),
        onclick: () => {
            const m = Store.matchToBackdrop(store);
            if (!m) return toast(T('err.match_nothing'), 'warning');
            if (!m.changed) return toast(T('msg.match_already'), 'info');
            toast(T('msg.match_done').replace(':ev', (m.exposure > 0 ? '+' : '') + m.exposure.toFixed(2)), 'success');
        },
    });

    const on = !!l.shadow;
    const toggle = P.el('label', { class: 'pe-check' }, [
        P.el('input', {
            type: 'checkbox', checked: on,
            onchange: (e) => Store.setShadow(store, e.target.checked ? {} : null),
        }),
        P.el('span', { text: T('ui.shadow') }),
    ]);

    const sh = l.shadow;
    const sl = (key, min, max, def, val, apply) => P.slider(
        { key, min, max, step: 1, def },
        T, val,
        (v) => Store.setShadow(store, apply(v), { live: true }),
        (v) => { Store.setShadow(store, apply(v)); Store.endGesture(store); });

    return P.el('div', { class: 'pe-placed' }, [
        P.el('div', { class: 'pe-row pe-row-wrap' }, [match]),
        toggle,
        sh ? sl('shadow_blur', 0, 200, 24, sh.blur, (v) => ({ blur: v })) : null,
        sh ? sl('shadow_y', -120, 120, 14, sh.y, (v) => ({ y: v })) : null,
        sh ? sl('shadow_x', -120, 120, 0, sh.x, (v) => ({ x: v })) : null,
        sh ? sl('shadow_opacity', 0, 100, 45, Math.round(sh.opacity * 100), (v) => ({ opacity: v / 100 })) : null,
        sh ? P.el('div', { class: 'pe-row' }, [
            P.el('input', {
                type: 'color', class: 'pe-color', value: sh.color, 'aria-label': T('ui.shadow_color'),
                oninput: (e) => Store.setShadow(store, { color: e.target.value }, { live: true }),
                onchange: (e) => { Store.setShadow(store, { color: e.target.value }); Store.endGesture(store); },
            }),
            P.el('span', { class: 'pe-hint', text: T('hint.shadow') }),
        ]) : null,
    ]);
}

function textProps(l) {
    const t = l.text;
    const set = (patch, live) => Store.updateActive(store, { text: { ...t, ...patch } }, { live, key: 'text' });
    return P.el('div', { class: 'pe-textprops' }, [
        P.el('textarea', {
            class: 'pe-input pe-textarea', rows: 2, 'aria-label': T('ui.text_value'),
            oninput: (e) => set({ value: e.target.value }, true),
            onchange: (e) => { set({ value: e.target.value }); Store.endGesture(store); },
        }, [t.value]),
        P.el('div', { class: 'pe-row' }, [
            P.el('input', { type: 'color', class: 'pe-color', value: t.color, 'aria-label': T('ui.text_color'), oninput: (e) => set({ color: e.target.value }, true) }),
            P.el('input', { type: 'color', class: 'pe-color', value: t.stroke.color, 'aria-label': T('ui.stroke_color'), oninput: (e) => set({ stroke: { ...t.stroke, color: e.target.value } }, true) }),
            P.el('select', {
                class: 'pe-select', 'aria-label': T('ui.align'), onchange: (e) => set({ align: e.target.value }),
            }, ['left', 'center', 'right'].map((a) => P.el('option', { value: a, text: T('align.' + a), selected: a === t.align }))),
        ]),
        P.slider({ key: 'font_size', min: 8, max: 400, step: 1, def: 64 }, T, t.size,
            (v) => set({ size: v }, true), (v) => { set({ size: v }); Store.endGesture(store); }),
        P.slider({ key: 'stroke_width', min: 0, max: 40, step: 1, def: 0 }, T, t.stroke.width,
            (v) => set({ stroke: { ...t.stroke, width: v } }, true),
            (v) => { set({ stroke: { ...t.stroke, width: v } }); Store.endGesture(store); }),
        P.slider({ key: 'shadow_blur', min: 0, max: 60, step: 1, def: 0 }, T, t.shadow.blur,
            (v) => set({ shadow: { ...t.shadow, blur: v } }, true),
            (v) => { set({ shadow: { ...t.shadow, blur: v } }); Store.endGesture(store); }),
    ]);
}

/**
 * The recipe, as a visible list.
 *
 * This is the tool's whole argument made concrete: every heavy step that
 * happened is a row you can switch off, and the picture goes back to what it
 * was without redoing anything else. In a file-based chain the only way to
 * revisit step 2 is to start over.
 */
function opsList(l) {
    return P.el('div', { class: 'pe-ops' }, [
        P.el('div', { class: 'pe-ops-title', text: T('ui.steps') }),
        ...l.ops.map((op) => P.el('div', { class: 'pe-op' + (op.enabled ? '' : ' is-off') + (op.error ? ' is-err' : '') }, [
            P.el('button', {
                class: 'pe-op-tog', type: 'button', text: op.enabled ? '✓' : '',
                'aria-label': T('ui.toggle_step'), 'aria-pressed': String(!!op.enabled),
                onclick: () => Store.toggleOp(store, op.id),
            }),
            P.el('span', { class: 'pe-op-name', text: T('op.' + op.kind) }),
            op.error ? P.el('span', { class: 'pe-op-err', text: T('err.step'), title: op.error }) : null,
            P.el('button', { class: 'pe-op-del', type: 'button', text: '×', 'aria-label': T('ui.remove_step'), onclick: () => Store.removeOp(store, op.id) }),
        ])),
    ]);
}

/* ─── AI panel ────────────────────────────────────────────────────────────── */

function buildAiPanel() {
    const pane = $('peAi');
    if (!pane) return;
    ui.aiButtons = {};

    const mk = (kind, opts) => {
        const o = opts || {};
        const btn = P.el('button', {
            class: 'pe-aibtn', type: 'button', 'data-op': kind,
            onclick: () => runOp(kind, o.params),
        }, [
            P.el('span', { class: 'pe-aibtn-title', text: T(o.titleKey || ('op.' + kind)) }),
            P.el('span', { class: 'pe-aibtn-desc', text: T(o.descKey || ('opdesc.' + kind)) }),
        ]);
        ui.aiButtons[o.id || kind] = btn;
        return btn;
    };

    // A command button looks identical to an op button on purpose: from the
    // user's side "whiten the backdrop" is the same kind of thing as "remove the
    // background", and which of the two happens to be a single model call is our
    // problem, not theirs.
    const mkCmd = (id, run) => {
        const btn = P.el('button', {
            class: 'pe-aibtn', type: 'button', 'data-cmd': id,
            onclick: () => runCmd(id, run),
        }, [
            P.el('span', { class: 'pe-aibtn-title', text: T('cmd.' + id) }),
            P.el('span', { class: 'pe-aibtn-desc', text: T('cmddesc.' + id) }),
        ]);
        ui.aiButtons[id] = btn;
        return btn;
    };

    ui.busyBox = P.el('div', { class: 'pe-busy', hidden: true });

    pane.append(
        ui.busyBox,
        P.el('div', { class: 'pe-aigrid' }, [
            // Two buttons, not a hidden setting. The models genuinely differ in
            // what they can see and in what they cost to download, and hiding
            // that behind one button means a person cutting out a bicycle waits
            // for a 14 MB model that was never going to find it.
            mk('bg-remove', { id: 'bg-people', params: { subject: 'people' } }),
            mk('bg-remove', {
                id: 'bg-any', params: { subject: 'any' },
                titleKey: 'op.bg_remove_any', descKey: 'opdesc.bg_remove_any',
            }),
            mkCmd('backdrop-white', () => Store.whitenBackdrop(store)),
            mkCmd('portrait-crop', () => Store.cropToPortrait(store)),
            mk('face-restore'),
            mk('colorize'),
            mk('upscale', { params: { scale: 4 } }),
            mk('erase'),
            mk('redact'),
        ]),
        P.el('p', { class: 'pe-hint', text: T('hint.ai_layers') }),
    );
}

/**
 * Run a composite command and say what happened.
 *
 * Commands can succeed at running and still fail at the user's goal — no face
 * to frame on, no backdrop to whiten, a backdrop too dark to reach white. Each
 * of those gets its own sentence, because "не удалось" for all three sends
 * someone to retry the thing that cannot work.
 */
async function runCmd(id, run) {
    funnel('cmd_start', { cmd: id });
    try {
        const r = await run();
        if (!r || !r.ok) {
            const reason = (r && r.reason) || 'failed';
            toast(T('err.cmd_' + reason) || T('err.op_unavailable'), 'warning');
            funnel('cmd_empty', { cmd: id, reason });
            return;
        }
        // A capped lift means the backdrop was too dark to reach white. It is
        // still an improvement and still worth keeping — but saying "готово"
        // would be a lie the user discovers by zooming in.
        if (r.capped) {
            toast(T('msg.backdrop_capped'), 'warning');
            funnel('cmd_capped', { cmd: id, ev: r.exposure });
        } else {
            toast(T('msg.cmd_done').replace(':cmd', T('cmd.' + id)), 'success');
            funnel('cmd_done', { cmd: id, ev: r.exposure, residual: r.residual });
        }
    } catch (e) {
        const msg = String(e && e.message || e);
        if (msg === 'needs_image_layer') toast(T('err.needs_image_layer'), 'warning');
        else toast(T('err.op_unavailable'), 'error');
        funnel('cmd_error', { cmd: id, err: msg.slice(0, 60) });
    }
}

async function runOp(kind, params) {
    const impl = OPS[kind];
    if (!impl) return;
    const avail = store.opsAvailable[kind];
    if (avail && avail.ok === false) { toast(T('err.op_' + avail.reason) || T('err.op_unavailable'), 'error'); return; }
    funnel('op_start', { op: kind });
    try {
        const r = await Store.applyOp(store, kind, params || {});
        // An operation can succeed and still have found nothing to do. Saying so
        // is not a failure message, it is the next step — and it is the
        // difference between "the tool is broken" and "this needs the brush".
        const info = (r.ops || []).map((o) => o.out && o.out.info).filter(Boolean).pop();
        if (r.ok && info && info.subject === false) {
            toast(T('msg.no_subject'), 'warning');
            funnel('op_no_subject', { op: kind });
        } else if (r.ok && info && info.faces === 0) {
            toast(T('msg.no_faces'), 'warning');
            funnel('op_no_faces', { op: kind });
        } else if (r.ok) {
            toast(T('msg.op_done').replace(':op', T('op.' + kind)), 'success');
            funnel('op_done', { op: kind });
        } else {
            toast(T('err.op_failed').replace(':op', T('op.' + kind)), 'error');
            funnel('op_error', { op: kind, err: r.error });
        }
    } catch (e) {
        const msg = String(e && e.message || e);
        if (msg === 'needs_selection') toast(T('err.needs_selection'), 'warning');
        else if (msg === 'needs_image_layer') toast(T('err.needs_image_layer'), 'warning');
        else toast(T('err.op_failed').replace(':op', T('op.' + kind)), 'error');
        funnel('op_error', { op: kind, err: msg.slice(0, 60) });
    }
}

function renderBusy() {
    const b = store.busy;
    if (!ui.busyBox) return;
    ui.busyBox.hidden = !b;
    if (!b) { ui.busyBox.textContent = ''; return; }
    ui.busyBox.textContent = '';
    ui.busyBox.append(
        P.el('div', { class: 'pe-busy-text', text: T('op.' + b.op) + ' · ' + Math.round((b.fraction || 0) * 100) + '%' }),
        P.el('div', { class: 'pe-busy-bar' }, [P.el('i', { style: 'width:' + Math.round((b.fraction || 0) * 100) + '%' })]),
    );
}

/* ─── export panel ────────────────────────────────────────────────────────── */

function buildExportPanel() {
    const pane = $('peExport');
    if (!pane) return;

    ui.exportInfo = P.el('div', { class: 'pe-expinfo' });
    ui.exportWarn = P.el('div', { class: 'pe-warn', hidden: true });

    const fmt = P.formatSelect(T, exportOpts.format, async (v) => {
        exportOpts.format = v;
        if (!await formatSupported(v)) {
            toast(T('err.format_unsupported').replace(':f', v.toUpperCase()), 'warning');
        }
        syncExport();
    });
    const preset = P.presetSelect(T, exportOpts.preset, (v) => { exportOpts.preset = v; syncExport(); });

    ui.qualityRow = P.slider({ key: 'quality', min: 40, max: 100, step: 1, def: 92 }, T, 92,
        () => { }, (v) => { exportOpts.quality = v / 100; syncExport(); });

    const target = P.el('input', {
        type: 'number', class: 'pe-input pe-input-num', min: 0, step: 10, value: 0,
        'aria-label': T('exp.target_kb'), placeholder: '0',
        onchange: (e) => { exportOpts.targetKB = Math.max(0, +e.target.value || 0); syncExport(); },
    });

    const go = P.el('button', { class: 'pe-btn pe-btn-primary pe-btn-wide', type: 'button', text: T('ui.export'), onclick: doExport });
    ui.batchBar = P.el('div', { class: 'pe-batch', hidden: true });

    pane.append(
        P.el('label', { class: 'pe-field' }, [P.el('span', { text: T('exp.preset') }), preset]),
        P.el('label', { class: 'pe-field' }, [P.el('span', { text: T('exp.format') }), fmt]),
        ui.qualityRow,
        P.el('label', { class: 'pe-field' }, [P.el('span', { text: T('exp.target_kb') }), target]),
        ui.exportInfo,
        ui.exportWarn,
        go,
        ui.batchBar,
    );
    syncExport();
}

function currentExportSize() {
    const p = EXPORT_PRESETS.find((x) => x.id === exportOpts.preset) || EXPORT_PRESETS[0];
    return presetSize(p, store.doc.w, store.doc.h);
}

function syncExport() {
    if (!ui.exportInfo) return;
    const size = currentExportSize();
    const f = FORMATS[exportOpts.format];
    ui.qualityRow.hidden = !f.lossy;
    ui.exportInfo.textContent = `${size.w} × ${size.h} · ${(size.w * size.h / 1e6).toFixed(1)} MP`;

    const warns = exportWarnings(store.doc, exportOpts.format, size);
    ui.exportWarn.hidden = !warns.length;
    ui.exportWarn.textContent = warns.map((w) => T('warn.' + w)).join(' · ');
}

async function doExport() {
    const size = currentExportSize();
    // The cap is spent on a DELIVERED result and keyed by SOURCE FILE, so
    // exporting the same photo as a PNG and then a JPEG is one job, not two.
    // Charging per click would make the number on the label meaningless.
    const key = store.doc.meta.name || 'doc';
    const limit = window.__toolLimit;
    let token = null;
    if (limit && limit.begin) {
        token = limit.begin(SLUG, [key]);
        if (token === false) return;
    }

    stage('exporting');
    try {
        const res = await exportDoc(store.doc, store.reg, {
            format: exportOpts.format,
            quality: exportOpts.quality,
            targetBytes: exportOpts.targetKB ? exportOpts.targetKB * 1024 : 0,
            size,
            name: store.doc.meta.name || 'photo',
            onProgress: () => { },
        });
        // Когда сзади идёт пачка, этот файл НЕ отдаётся отдельно: он уйдёт
        // вместе со всеми одним архивом. Раньше его скачивали здесь И клали
        // в архив — две выдачи на ОДИН жест, а браузер даёт только одну:
        // архив со всей пачкой уходил в придержанные, а человек получал одно фото.
        if (!batchFiles.length) download(res.blob, res.filename);
        if (res.overTarget) toast(T('msg.over_target'), 'warning');
        toast(T('msg.exported').replace(':size', P.fmtBytes(res.bytes)), 'success');
        token && token.commit && token.commit();
        try {
            window.__aeToolComplete && window.__aeToolComplete({
                format: exportOpts.format, w: res.width, h: res.height, bytes: res.bytes,
                layers: store.doc.layers.length,
                ops: store.doc.layers.reduce((n, l) => n + (l.ops ? l.ops.length : 0), 0),
            });
        } catch (_) { }
        if (batchFiles.length) await runBatch(res);
    } catch (e) {
        // The run was not delivered, so it is not charged. Abort, never commit.
        token && token.abort && token.abort();
        console.error(e);
        toast(T('err.export'), 'error');
    }
}

/**
 * Batch: the same recipe over the rest of the files.
 *
 * Not a separate mode with its own screen — it is literally the document's
 * layer 0 swapped for each file and the recipe re-run. That is only possible
 * because the edits are a description rather than pixels, and it is why "apply
 * my grade to 200 photos" costs no extra design.
 */
async function runBatch(firstResult) {
    if (!batchFiles.length) return;
    const files = batchFiles.slice();
    batchFiles = [];
    syncBatchBar();

    // Всё готовое отдаёт ОДНИМ архивом общий примитив (потолок, честный отказ,
    // РАЗНЫЕ имена). Свой JSZip здесь срабатывал, когда библиотека уже была
    // загружена (после сохранения проекта), и «IMG_1.heic» + «IMG_1.jpg»
    // давали два «IMG_1.jpg» — JSZip.file() молча оставлял один.
    const loose = [];
    if (firstResult) loose.push({ name: firstResult.filename, blob: firstResult.blob });

    let done = 0;
    for (const f of files) {
        try {
            const d = await decodeFile(f, {});
            const sub = Store.createStore();
            Store.openImage(sub, d.drawable, { name: d.name, hasAlpha: d.hasAlpha });
            // Carry over everything that is not tied to this photo's pixels.
            const src = store.doc.layers[0];
            let doc = Doc.updateLayer(sub.doc, sub.doc.layers[0].id, {
                grade: src.grade, spatial: src.spatial, gradeKey: 'batch',
            });
            for (const l of store.doc.layers.slice(1)) {
                if (l.type === 'image') continue;                 // a pasted photo is not part of a recipe
                doc = Doc.addLayer(doc, { ...l, id: null });
            }
            sub.doc = doc;
            const size = presetSize(EXPORT_PRESETS.find((x) => x.id === exportOpts.preset) || EXPORT_PRESETS[0], doc.w, doc.h);
            const res = await exportDoc(doc, sub.reg, {
                format: exportOpts.format, quality: exportOpts.quality,
                targetBytes: exportOpts.targetKB ? exportOpts.targetKB * 1024 : 0,
                size, name: d.name,
            });
            loose.push({ name: res.filename, blob: res.blob });
        } catch (e) {
            console.warn('[photo-editor] batch item failed', f.name, e);
        }
        done++;
        if (ui.batchBar) ui.batchBar.textContent = T('msg.batch_progress').replace(':d', done).replace(':n', files.length);
    }

    if (loose.length) {
        // Не цикл загрузок: браузер отдаёт ОДИН файл на жест, остальные
        // выбрасывает и после этого перестаёт скачивать вообще (так /equalizer
        // отдавал 1 трек из 17).
        const BD = await (window.__ensureBatchDeliver ? window.__ensureBatchDeliver() : Promise.resolve(null));
        if (BD) await BD.deliver(loose, { slug: 'photo-editor', zipName: (store.doc.meta.name || 'photos') + '-batch.zip', archiveOnly: true });
    }
    if (ui.batchBar) ui.batchBar.hidden = true;
    toast(T('msg.batch_done').replace(':n', String(done)), 'success');
}

function syncBatchBar() {
    if (!ui.batchBar) return;
    ui.batchBar.hidden = !batchFiles.length;
    if (batchFiles.length) {
        ui.batchBar.textContent = T('msg.batch_queued').replace(':n', String(batchFiles.length));
    }
}

/* ─── project ─────────────────────────────────────────────────────────────── */

async function doSaveProject() {
    if (!store.doc.layers.length) return;
    try {
        const blob = await saveProject(store.doc, store.reg, store.sources);
        download(blob, (store.doc.meta.name || 'project') + '.' + PROJECT_EXT);
        toast(T('msg.project_saved'), 'success');
    } catch (e) {
        toast(T('err.project_save'), 'error');
        console.error(e);
    }
}

async function openProject(file) {
    try {
        const { doc, sources, masks, missing } = await loadProject(file);
        store.doc = doc;
        store.reg = (await import('./render/compose.js')).createRegistry();
        store.sources = new Map();
        for (const [id, blob] of sources) {
            const d = await decodeFile(new File([blob], id, { type: blob.type }), {});
            store.reg.bitmaps.set(id, d.drawable);
            store.sources.set(id, blob);
        }
        for (const [id, m] of masks) store.reg.masks.set(id, m);
        store.hist = Store.Hist.createHistory(doc);
        Store.emit(store, 'open');
        showEditor();
        if (missing && missing.length) toast(T('err.project_missing').replace(':n', String(missing.length)), 'warning');
        else toast(T('msg.project_opened'), 'success');
    } catch (e) {
        toast(T('err.project_open'), 'error');
        console.error(e);
    }
}

/* ─── sync ────────────────────────────────────────────────────────────────── */

function syncPanels() {
    const g = gradeOf(), s = spatialOf();
    for (const [key, row] of Object.entries(ui.sliders || {})) {
        if (!row.__set) continue;
        const v = (key in g) ? g[key] : s[key];
        if (v != null) row.__set(v);
    }
    if (ui.hsl && ui.hsl.__rebuild) ui.hsl.__rebuild();
    if (ui.curve) ui.curve.redraw();
    if (ui.histCanvas) P.drawHistogram(ui.histCanvas, Store.currentHistogram(store), null);
    renderLayers();
    syncExport();

    const canUndo = store.hist.index > 0;
    const canRedo = store.hist.index < store.hist.stack.length - 1;
    $('peUndo') && ($('peUndo').disabled = !canUndo);
    $('peRedo') && ($('peRedo').disabled = !canRedo);

    // Keyed by BUTTON id, not by op kind: background removal is one op with two
    // buttons whose models differ in what they need, so "any subject" can be
    // unavailable while "people" is fine. Greying both would hide a capability
    // that works.
    // A command is only as available as the model underneath it: whitening the
    // backdrop needs the same matte as removing it, and framing a portrait needs
    // the face detector. Leaving them lit when the thing they call cannot run
    // trades an honest grey button for a click that fails.
    const DEPENDS = {
        'bg-people': 'bg-remove', 'bg-any': 'bg-remove',
        'backdrop-white': 'bg-remove', 'portrait-crop': 'face-restore',
    };
    for (const [id, btn] of Object.entries(ui.aiButtons || {})) {
        const a = store.opsAvailable[DEPENDS[id] || id];
        const extra = id === 'bg-any' ? store.opsAvailableAny : null;
        const state = extra || a;
        const off = state && state.ok === false;
        btn.classList.toggle('is-off', !!off);
        btn.title = off ? (T('err.op_' + state.reason) || T('err.op_unavailable')) : '';
    }
}

function onEyedrop(c) {
    const l = Doc.activeLayer(store.doc);
    if (l && l.type === 'text') Store.updateActive(store, { text: { ...l.text, color: c.hex } });
    else if (l && l.type === 'shape') Store.updateActive(store, { shape: { ...l.shape, fill: c.hex } });
    toast(c.hex, 'info');
}

function download(blob, name) {
    if (window.__downloadFile) window.__downloadFile(blob, name);
    else {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob); a.download = name;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    }
}

/* Exposed for the Playwright suite: driving the real UI is the point, but a few
   assertions need to read state that has no visible representation (which
   render path ran, how many undo steps exist). Read-only. */
window.__PE = {
    store, get vp() { return vp; }, Doc, Mask, Store, View,
    exportDoc, presetSize, EXPORT_PRESETS,
};
